/**
 * Number/currency/date formatters used across admin pages.
 * All formatters are locale-aware and tabular-num friendly.
 */

const numFmt = new Intl.NumberFormat('en-US');
const compactFmt = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const usdFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const usdInt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 2 });

/** "12345" → "12,345". null/undefined → em-dash. */
export const fmtNumber = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(n)) return '—';
  return numFmt.format(n);
};

/** "12345" → "12.3K". Use for compact UI cells. */
export const fmtCompact = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(n)) return '—';
  return compactFmt.format(n);
};

/** "12.5" → "$12.50". null/undefined → em-dash. */
export const fmtCurrency = (
  amount: number | string | null | undefined,
  opts: { compactZero?: boolean; whole?: boolean } = {}
): string => {
  if (amount == null) return '—';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(num)) return '—';
  if (opts.compactZero && num === 0) return '—';
  return (opts.whole ? usdInt : usdFmt).format(num);
};

/** "0.124" → "12.4%". */
export const fmtPercent = (
  ratio: number | null | undefined,
  opts: { fromPercent?: boolean } = {}
): string => {
  if (ratio == null || Number.isNaN(ratio)) return '—';
  const v = opts.fromPercent ? ratio / 100 : ratio;
  return pctFmt.format(v);
};

/** Compute a signed delta with sign + percent label. */
export const fmtDelta = (
  current: number | null | undefined,
  previous: number | null | undefined,
  opts: { invert?: boolean } = {}
): { label: string; ratio: number; trend: 'up' | 'down' | 'flat' } => {
  if (current == null || previous == null || previous === 0) {
    return { label: '—', ratio: 0, trend: 'flat' };
  }
  const ratio = (current - previous) / Math.abs(previous);
  const trendRaw: 'up' | 'down' | 'flat' =
    Math.abs(ratio) < 0.001 ? 'flat' : ratio > 0 ? 'up' : 'down';
  const trend = opts.invert && trendRaw !== 'flat'
    ? (trendRaw === 'up' ? 'down' : 'up')
    : trendRaw;
  const sign = ratio > 0 ? '+' : '';
  return {
    label: `${sign}${(ratio * 100).toFixed(1)}%`,
    ratio,
    trend
  };
};

/** ISO timestamp → "2 hours ago" / "Apr 5, 14:30" style. */
const dtFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
});
export const fmtRelative = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return dtFmt.format(date);
};

/** Absolute date formatter — for tables where freshness < ranking matters. */
export const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return dtFmt.format(d);
};
